import { memo, useState } from 'react'
import { Box, Stack } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import { useProductContext } from 'context/ProductContext'
import { usePublishProduct, useSyncProductToGraph, useUnpublishProduct } from 'hooks/useProduct'
import CustomDialog from 'components/dialogs/CustomDialog'
import { snackbarService } from 'services/snackbarService'

// The two things done to a product as a whole, rather than to one of its
// sections: put it in front of shoppers, and write it into the knowledge graph.
// They sit at the foot of the page instead of in the Publishing section's header
// because that section reports what the product's state is - these change it.
function ProductActions() {
  const { product, refetch: refetchProduct } = useProductContext();
  const isPublished = !!product.publishedAt;
  // Publishing puts the product in front of shoppers and unpublishing takes it
  // away from them, and both happen on one click from a page somebody is reading
  // rather than acting on - so the click is asked about rather than obeyed.
  const [confirming, setConfirming] = useState(false);

  // A failed action said nothing at all before this: the handlers logged to the
  // console, so a sync that threw looked exactly like one that had not been
  // pressed.
  const failed = (what: string) => (error: any) =>
    snackbarService.showSnackbar(
      `${what} - ${error?.response?.data?.message ?? error?.message ?? 'unknown error'}`,
      'error',
    );


  const { mutate: publishMutation, isLoading: isPublishing } = usePublishProduct(
    () => {
      snackbarService.showSnackbar('Product has been published', 'success');
      refetchProduct();
    },
    failed('Could not change publishing'),
  );

  const { mutate: unpublishMutation, isLoading: isUnpublishing } = useUnpublishProduct(
    () => {
      snackbarService.showSnackbar('Product has been unpublished', 'success');
      refetchProduct();
    },
    failed('Could not change publishing'),
  );

  // Re-runnable on purpose, published or not: a product whose attributes were
  // corrected after the first sync needs a second one to carry the correction
  // over, which is what the Publishing section's Outdated is saying.
  const { mutate: syncMutation, isLoading: isSyncing } = useSyncProductToGraph(
    () => {
      snackbarService.showSnackbar('Product has been synced to the knowledge graph', 'success');
      refetchProduct();
    },
    failed('Could not sync the product'),
  );

  const isSynced = !!product.graphSyncedAt;

  return (
    <Stack direction="row" gap={2}>
      {/* Half the row each. Sized from the wrapper rather than with an sx on the
          button: CustomButton spreads its props after its own sx, so one passed
          in would replace its styling wholesale. */}
      <Box sx={{ flex: 1 }}>
        <CustomButton
          text={isSynced ? 'Re-sync to graph' : 'Sync to graph'}
          variant="outlined"
          onClick={() => syncMutation(product.id)}
          isLoading={isSyncing}
          disabled={isPublishing || isUnpublishing}
          fullWidth
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <CustomButton
          text={isPublished ? 'Unpublish' : 'Publish'}
          intent={isPublished ? 'error' : 'primary'}
          onClick={() => setConfirming(true)}
          isLoading={isPublishing || isUnpublishing}
          disabled={isSyncing}
          fullWidth
        />
      </Box>
      <CustomDialog
        open={confirming}
        title={isPublished ? 'Unpublish' : 'Publish'}
        message={
          isPublished
            ? 'Shoppers will no longer see this product. Unpublish it?'
            : 'Shoppers will be able to see and buy this product. Publish it?'
        }
        buttons={[
          <CustomButton
            key="cancel"
            text="Cancel"
            variant="outlined"
            onClick={() => setConfirming(false)}
            disabled={isPublishing || isUnpublishing}
          />,
          <CustomButton
            key="confirm"
            text={isPublished ? 'Unpublish' : 'Publish'}
            intent={isPublished ? 'error' : 'primary'}
            isLoading={isPublishing || isUnpublishing}
            onClick={() => {
              setConfirming(false);
              isPublished ? unpublishMutation(product.id) : publishMutation(product.id);
            }}
          />,
        ]}
      />
    </Stack>
  )
}

export default memo(ProductActions)
